import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { mcAdminAPI } from '../../services/api';

export function MCMembersPanel({ communityId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', villa_number: '' });

  const loadMembers = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    try {
      const res = await mcAdminAPI.list(communityId);
      setMembers(res.data || []);
    } catch {
      toast.error('Failed to load committee members');
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and role are required');
      return;
    }
    setSaving(true);
    try {
      await mcAdminAPI.add(communityId, {
        name: form.name.trim(),
        role: form.role.trim(),
        villa_number: form.villa_number.trim(),
      });
      toast.success('Member added');
      setForm({ name: '', role: '', villa_number: '' });
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this committee member?')) return;
    try {
      await mcAdminAPI.remove(communityId, memberId);
      toast.success('Member removed');
      loadMembers();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  return (
    <div data-testid="mc-members-panel" className="space-y-3 pt-4 border-t border-slate-700/50">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-pink-400" />
        <h4 className="text-sm font-semibold text-pink-400">Management Committee Members</h4>
      </div>
      <p className="text-[10px] text-slate-500 leading-tight">
        These members appear in the resident dashboard when the MC List feature is enabled.
      </p>

      {/* Add form */}
      <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] text-slate-400 uppercase">Name</Label>
            <Input
              data-testid="mc-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., John Doe"
              className="h-8 text-xs bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-400 uppercase">Role</Label>
            <Input
              data-testid="mc-form-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="e.g., President"
              className="h-8 text-xs bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-400 uppercase">Villa</Label>
            <Input
              data-testid="mc-form-villa"
              value={form.villa_number}
              onChange={(e) => setForm({ ...form, villa_number: e.target.value })}
              placeholder="e.g., A-12"
              className="h-8 text-xs bg-slate-900 border-slate-600 text-white"
            />
          </div>
        </div>
        <Button
          data-testid="mc-add-button"
          onClick={handleAdd}
          disabled={saving}
          size="sm"
          className="w-full bg-gk-teal hover:bg-gk-teal-hover h-7 text-xs"
        >
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
          Add Member
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gk-teal" />
        </div>
      ) : members.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-3">No committee members added yet.</p>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => (
            <div
              key={m.id}
              data-testid={`mc-member-${m.id}`}
              className="flex items-center justify-between p-2.5 bg-slate-800/60 rounded border border-slate-700/60"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-white truncate">{m.name}</p>
                <p className="text-[11px] text-pink-300">{m.role}</p>
                {m.villa_number && (
                  <p className="text-[10px] text-slate-400">Villa {m.villa_number}</p>
                )}
              </div>
              <Button
                data-testid={`mc-remove-${m.id}`}
                onClick={() => handleRemove(m.id)}
                size="sm"
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
